
import {
  defineComponent,
  reactive,
  toRefs,
  onMounted,
  computed,
  Ref
} from "vue";
import { useAuth } from "../../../modules/auth";
import { useToast } from "../../../modules/message";
import { format, formatDate } from "../../../modules/utils";
import { ClientePayload } from "../../../modules/business/cliente/clienteBusiness";
import {
  ClienteRepresentadoPayload,
  useClienteRepresentadoService
} from "../../../modules/business/cliente/clienteRepresentadoBusiness";

// eslint-disable-next-line
declare var require: any;

interface ClienteRepresentadosState {
  representados: Array<ClienteRepresentadoPayload>;
  original: Array<number>;
  selectorKey: number;
  stateReady: boolean;
  loadings: Array<Ref<boolean>>;
}

export default defineComponent({
  props: {
    idCliente: { type: Number, required: true },
    parentLoading: Boolean
  },
  setup(props) {
    const state = reactive<ClienteRepresentadosState>({
      representados: [],
      original: [],
      selectorKey: 0,
      stateReady: false,
      loadings: []
    });

    const stateLoading = computed(() => {
      return state.loadings.length !== 0 || !!props.parentLoading;
    });

    const stateBlocked = computed(() => {
      return !!(
        stateLoading.value || !useAuth().amIAllowed("edit", "root-cliente")
      );
    });

    const setRepresentados = (
      representados: Array<ClienteRepresentadoPayload>
    ) => {
      state.representados = representados;
      state.original = representados.map((r) => r.idCliente);
    };

    const adicionarRepresentado = (cliente?: ClientePayload | null) => {
      // remonta o seletor para ficar vazio e pronto para o próximo
      state.selectorKey++;
      if (!cliente || !cliente.idCliente) {
        return;
      }
      if (cliente.idCliente === props.idCliente) {
        useToast().warning("O parceiro não pode representar a si mesmo");
        return;
      }
      if (state.representados.some((r) => r.idCliente === cliente.idCliente)) {
        useToast().warning("Cliente já está na lista");
        return;
      }
      state.representados.push({
        idCliente: cliente.idCliente,
        nome: cliente.nome,
        cpfCnpj: cliente.cpfCnpj,
        bloqueado: cliente.bloqueado
      });
    };

    const removerRepresentado = (representado: ClienteRepresentadoPayload) => {
      state.representados = state.representados.filter(
        (r) => r.idCliente !== representado.idCliente
      );
    };

    // pessoa física vem com o CPF do responsável (11 dígitos)
    const formatCpfCnpj = (cpfCnpj?: string | null) => {
      if (!cpfCnpj) {
        return "";
      }
      return cpfCnpj.length === 11
        ? format("###.###.###-##", cpfCnpj)
        : format("##.###.###/####-##", cpfCnpj);
    };

    onMounted(() => {
      const {
        data,
        loading,
        getRepresentados
      } = useClienteRepresentadoService();
      state.loadings.push(loading);
      getRepresentados(props.idCliente)
        .then(() => {
          setRepresentados(data.value ?? []);
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          state.stateReady = true;
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    });

    // Chamado pelo CadastroCliente depois de salvar o cliente (botão único)
    const salvarRepresentados = (): Promise<void> => {
      if (!state.stateReady) {
        return Promise.resolve();
      }

      const atuais = state.representados.map((r) => r.idCliente);
      const alterou =
        atuais.length !== state.original.length ||
        atuais.some((id) => !state.original.includes(id));
      if (!alterou) {
        return Promise.resolve();
      }

      const {
        data,
        loading,
        updateRepresentados
      } = useClienteRepresentadoService();
      state.loadings.push(loading);
      return updateRepresentados(props.idCliente, state.representados)
        .then(() => {
          setRepresentados(data.value ?? []);
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        })
        .catch((e) => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          throw e;
        });
    };

    return {
      stateLoading,
      stateBlocked,
      adicionarRepresentado,
      removerRepresentado,
      salvarRepresentados,
      formatCpfCnpj,
      formatDate,
      ...toRefs(state)
    };
  },
  beforeCreate() {
    // import tardio: EntitySelectorCliente -> CrudCliente -> CadastroCliente -> este componente
    if (this.$options.components) {
      this.$options.components.EntitySelectorCliente = require("../EntitySelectorCliente.vue").default;
    }
  },
  components: {}
});
